import { Component, h, Host, Prop, Element, State, Listen } from '@stencil/core';
import {Logger, SectionBehavior, VerticalPositionBottom, VerticalPositionTop, Video} from '../../global/_types'
import { getSectionBehavior, isMobileViewport, renderBlockMargin } from '../../global/_utils';
import { customColors } from '../../global/_constants';

@Component({
  tag: 'cms-highlight-text',
  styleUrl: 'cms-highlight-text.scss',
  shadow: true
})
export class CmsHighlightText {
  @Element() host: HTMLElement;

  @Prop() config: string;
  @Prop() debug: boolean = false;
  @Prop() behavior: string = '{}';

  @State() isMobile: boolean = false;

  private logger: Logger;

  private _cfg: CmsHighlightTextConfig = {
      text: '',
      headline: '',
      disableHighlighting: false,
      textSize: {
          l: 80,
          m: 64,
          s: 32
      },
      desktop: {
          backgroundType: CmsHighlightTextBackgroundType.none,
          background: '',
          activeColor: customColors.black,
          inactiveAlpha: 60
      },
      mobile: {
          backgroundType: CmsHighlightTextBackgroundType.none,
          background: '',
          activeColor: customColors.black,
          inactiveAlpha: 60
      }
  }

  private desktopSectionStyle: any;
  private mobileSectionStyle: any;

  private desktopInactiveTextStyle: any = {
      color: '#00000099'
  }
  private desktopActiveTextStyle: any = {
      color: '#000000FF'
  }
  private mobileInactiveTextStyle: any = {
      color: '#00000099'
  }
  private mobileActiveTextStyle: any = {
      color: '#000000FF'
  }

  private hasMobileStyle: boolean = false;
  private desktopVideoConfig: any;
  private mobileVideoConfig: any;
  private sentences: NodeList;
  private parts = 0;
  private activePart = 0;
  private stepSize = 0;
  private stepSizeUp = 0;
  private stepSizeDown = 0;
  private windowY: number = 0;
  private scrollActive: boolean = false;

  private rootMarginTop: number;
  private rootMarginBottom: number;

  /* SectionBehavior */
  private sectionBehavior: SectionBehavior;

  private videoElement: HTMLVideoElement;
  private contentElement: HTMLDivElement;


  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsHighlightText');

    const providedConfig = JSON.parse(this.config);

    this.hasMobileStyle = !!providedConfig.mobile;
    this.isMobile = isMobileViewport();

    // Set backgroundStyle
    providedConfig.desktop = { ...this._cfg.desktop, ...providedConfig.desktop}
    providedConfig.mobile = { ...this._cfg.mobile, ...providedConfig.desktop, ...providedConfig.mobile}
    providedConfig.desktop.backgroundType = providedConfig.desktop.backgroundType ?
        CmsHighlightTextBackgroundType[providedConfig.desktop.backgroundType] :
        CmsHighlightTextBackgroundType.none;
    if (this.hasMobileStyle) {
        providedConfig.mobile.backgroundType = providedConfig.mobile.backgroundType ?
            CmsHighlightTextBackgroundType[providedConfig.mobile.backgroundType] :
            CmsHighlightTextBackgroundType.none;
    }

    this._cfg = { ...this._cfg, ...providedConfig}
    this.desktopSectionStyle = { ...this.desktopSectionStyle, ...this.defineSectionStyle(this._cfg.desktop, false)};
    this.mobileSectionStyle = { ...this.mobileSectionStyle, ...this.defineSectionStyle(this.hasMobileStyle ? this._cfg.mobile : this._cfg.desktop, true)};

    // Set textStyle
    this.defineTextStyle(false);
    this.defineTextStyle(true);

    // Section-Behavior
    this.sectionBehavior = getSectionBehavior(this.behavior);
  }

  componentDidLoad() {
    this.logger.trace('CmsHighlightText loaded');
    if (this._cfg.disableHighlighting) return;

    const rects: DOMRectList = this.host.getClientRects();
    if (rects && rects.length > 0) {
      const activationBorder = window.screen.height / 3;
      const height = rects.item(Math.min(1, rects.length - 1)).height;
      this.rootMarginTop = activationBorder;
      this.rootMarginBottom = height + this.rootMarginTop - window.screen.height + activationBorder;
      this.sentences = this.host.shadowRoot.querySelectorAll('.sentence');
      this.parts = this.sentences.length;
      this.stepSize = height / this.parts;
    }

    this.videoElement = this.host.shadowRoot.querySelector('video');
    if (this.videoElement) {
        this.contentElement = this.host.shadowRoot.querySelector('.content');
        this.resizeContent();
    }
  }

  // Resize EventHandler
  @Listen('resize', {target: 'window'})
  updateViewport() {
    this.isMobile = isMobileViewport();
  }

  handleDataLoaded() {
    this.resizeContent();
  }

  private defineTextStyle(mobile: boolean) {
      const alpha = mobile ? this._cfg.mobile.inactiveAlpha : this._cfg.desktop.inactiveAlpha;
      let color = mobile ? this._cfg.mobile.activeColor : this._cfg.desktop.activeColor;

      // Transform three digit hex to six digit hex
      if (color.length == 4) {
          color = color.split("").map((item)=>{
              if(item == "#"){return item}
                  return item + item;
          }).join("");
      }
      if (mobile) {
          this.mobileActiveTextStyle.color = color + 'FF';
          this.mobileInactiveTextStyle.color = this._cfg.disableHighlighting ? this.mobileInactiveTextStyle.color : color + alpha.toString(16);

          this.mobileSectionStyle['--highlight--active-text-color'] = this.mobileActiveTextStyle.color;
          this.mobileSectionStyle['--highlight--inactive-text-color'] = this.mobileInactiveTextStyle.color;
      } else {
          this.desktopActiveTextStyle.color = color + 'FF';
          this.desktopInactiveTextStyle.color = this._cfg.disableHighlighting ? this.desktopActiveTextStyle.color : color + alpha.toString(16);

          this.desktopSectionStyle['--highlight--active-text-color'] = this.desktopActiveTextStyle.color;
          this.desktopSectionStyle['--highlight--inactive-text-color'] = this.desktopInactiveTextStyle.color;
      }
  }

  @Listen('resize', {target: 'window'})
  private resizeContent() {
      // Sync size of video and content
      if (!this.contentElement) return
      // Always get videoElement again, bc. it's not uptodate
      this.videoElement = this.host.shadowRoot.querySelector('video');


      this.contentElement.style.minHeight = this.videoElement.offsetHeight + 'px';
      if (this.videoElement.offsetHeight < this.contentElement.offsetHeight) {
          this.videoElement.style.minHeight = this.contentElement.offsetHeight + 'px';
      }
  }

  private defineSectionStyle(config: CmsHighlightTextViewportConfig, mobile: boolean): any {
      let localSectionStyle: any = {
          backgroundColor: '#fff',
          backgroundImage: 'none',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '50%',
          backgroundSize: 'cover'
      }

      const backgroundType = config.backgroundType;
      switch (backgroundType) {
          case CmsHighlightTextBackgroundType.color: localSectionStyle.backgroundColor = config.background; break;
          case CmsHighlightTextBackgroundType.gradient:
              localSectionStyle = {
                  background: config.background
              }
              break;
          case CmsHighlightTextBackgroundType.video:
              if (mobile) {
                  this.mobileVideoConfig = (this.hasMobileStyle ? config.background as Video : this._cfg.desktop.background);
              } else {
                  this.desktopVideoConfig = config.background as Video;
              }

              break;
          case CmsHighlightTextBackgroundType.image: localSectionStyle = {...localSectionStyle, ...{
              backgroundColor: '#fff',
              backgroundImage: `url("${config.background}")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '50%',
              backgroundSize: 'cover'
              }};
              break;
          case CmsHighlightTextBackgroundType.none: localSectionStyle.backgroundColor = 'transparent'; break;
      }
      return localSectionStyle;
  }

  @Listen('scroll', {target: 'window'})
  handleScroll() {
      // Reset active part
      const fromTop: boolean = this.windowY < window.scrollY;
      if (!this.scrollActive) {
          this.activePart = fromTop ? 0 : this.parts - 1;
      }

      // Check if component is visible
      const rects: DOMRectList = this.host.getClientRects();
      if (!rects || rects.length < 1) {
        return;
      }

      const rectTop = rects.item(0).top;
      const rectBottom = Math.ceil(rects.item(Math.min(2, rects.length - 1)).bottom);
      if (!this.scrollActive) {
          if ((fromTop && rectTop < this.rootMarginTop && rectTop > this.rootMarginTop - window.screen.height) ||
              (!fromTop && rectBottom > this.rootMarginBottom && rectBottom < this.rootMarginBottom + window.screen.height)) {
              this.scrollActive = true;
              if (fromTop) {
                  this.stepSizeUp = this.stepSize;
                  this.stepSizeDown = 0;
              } else {
                  this.stepSizeUp = 0;
                  this.stepSizeDown = this.stepSize;
              }
          } else {
              this.scrollActive = false;
          }
      }

      // Highlight is active
      if (this.scrollActive) {
          const delta = this.windowY - window.scrollY;
          const absoluteDelta = Math.ceil(Math.abs(delta));
          const directionUp = delta > 0;
          if (directionUp) {
              this.stepSizeUp += absoluteDelta;
              this.stepSizeDown -= absoluteDelta;
          } else {
              this.stepSizeUp -= absoluteDelta;
              this.stepSizeDown += absoluteDelta;
          }

          if (directionUp && this.stepSizeUp >= this.stepSize && this.activePart > 0) {
              this.activePart = Math.max(0 ,this.activePart - 1);
              this.stepSizeUp = Math.ceil(Math.abs(this.stepSizeUp) - this.stepSize);
              this.stepSizeDown = this.stepSize - this.stepSizeUp;
          } else if(!directionUp && this.stepSizeDown >= this.stepSize && this.activePart < (this.parts - 1)) {
              this.activePart = Math.min(this.parts - 1, this.activePart + 1);
              this.stepSizeDown = Math.ceil(Math.abs(this.stepSizeDown) - this.stepSize);
              this.stepSizeUp = this.stepSize - this.stepSizeDown;
          }

          this.sentences.forEach(x => (x as HTMLElement).classList.remove('text-active'));
          const activeSentence = (this.sentences[this.activePart] as HTMLElement);
          if (activeSentence) {
            activeSentence.classList.add('text-active');
          }
      }
      // TODO: Set activePart, if outside of visibility

      this.windowY = window.scrollY;
  }

  // Render
  render() {
      // Section behavior
      const sectionClass = `${this.sectionBehavior.sectionClass} container`;
      const videoConfig = this.isMobile ? this.mobileVideoConfig : this.desktopVideoConfig;
      const sectionStyle = this.isMobile ? this.mobileSectionStyle : this.desktopSectionStyle;
      const activeTextStyle = this.isMobile ? this.mobileActiveTextStyle : this.desktopActiveTextStyle;

      // Set font-sizes
      sectionStyle['--highlight--text-size-l'] = `${this._cfg.textSize.l}px`;
      sectionStyle['--highlight--text-size-m'] = `${this._cfg.textSize.m}px`;
      sectionStyle['--highlight--text-size-s'] = `${this._cfg.textSize.s}px`;

      return (
        <Host class={this.sectionBehavior.hostClass}>
          <section style={sectionStyle}  class={sectionClass}>
            { renderBlockMargin(this.sectionBehavior, VerticalPositionTop) }
            <div class="section-inner">
              {
              videoConfig ?
              <video playsinline autoplay muted loop onLoadedData={this.handleDataLoaded.bind(this)}>
                  {
                      videoConfig.webm ? <source src={videoConfig.webm} type='video/webm'/> : ''
                  }
                  {
                      videoConfig.mp4 ? <source src={videoConfig.mp4} type='video/mp4'/> : ''
                  }
                  Your browser does not support the video tag.
              </video> : ''
              }
              {(this._cfg.headline ? <div class="headline" style={activeTextStyle}>{this._cfg.headline}</div> : '')}
              <div class="content">
                  <div class="text-wrapper">
                  {
                      this._cfg.text.split('\\n')
                                    .filter(x => x.length > 1)
                                    .map(x => x.trim())
                                    .filter(x => x !== '')
                                    .map((x, i) => {
                          const isListItem =  x.startsWith('*');
                          if (isListItem) x = x.substring(1);
                          const textClass = `sentence ${i === 0 ? 'text-active' : ''} ${ isListItem ? 'li' : ''}`;
                          return (<span class={textClass} innerHTML={`${x} `}/>);
                      })
                  }
                  </div>
              </div>
            </div>
            { renderBlockMargin(this.sectionBehavior, VerticalPositionBottom) }
          </section>
        </Host>
      );
  }
}

interface CmsHighlightTextConfig {
    headline: string,
    text: string,
    disableHighlighting: boolean,
    textSize: CmsHighlightTextFontSize,
    desktop: CmsHighlightTextViewportConfig,
    mobile: CmsHighlightTextViewportConfig
}
interface CmsHighlightTextViewportConfig {
    backgroundType: CmsHighlightTextBackgroundType,
    background: any,
    activeColor: string,
    inactiveAlpha: number
}
interface CmsHighlightTextFontSize {
    l: number,
    m: number,
    s: number
}
enum CmsHighlightTextBackgroundType {
    none,
    color,
    gradient,
    video,
    image
}
